"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The permission catalogue. Resources declare actions; roles grant scopes.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **`decisions.md` R-4: THE MODEL IS OURS, because the mockup contradicts itself.** Both
 * encodings are in `docs/mockup/main.jsx`, and reading them side by side is what settles the
 * shape:
 *
 *  * **the live flat catalogue** (`PERM_CATALOG`, **25** keys) bakes the scope into the NAME —
 *    `worklog.own`, `worklog.team`, `worklog.all`, `worklog.edit.any`;
 *  * **the dead legacy tab** (`PERMS`, 11 actions) has `worklog.view` with a value per role of
 *    `workspace | csapat | saját | nincs`.
 *
 * Those are the same concept twice. `worklog.view` at `{workspace, csapat, saját}` **is**
 * `worklog.all / worklog.team / worklog.own`. So the flat keys are not 25 independent
 * permissions: several are one action at three scopes, and a flat checkbox grid cannot express
 * that — which is exactly why R-4 says the Jogosultságok screen must be redrawn rather than
 * transcribed.
 *
 * ⚠️ **CLAUDE.md rule 8 — never invent a permission action.** Every action below is derived from
 * the mockup's own keys. The two kinds of change are marked in place:
 *
 *  * **COLLAPSED** — several mockup keys that were one action at different scopes;
 *  * **ADDED** — genuinely new, flagged for a decision. There are two, and both are noted.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ADDED_ACTIONS = exports.MOCKUP_KEY_MAP = exports.ACTIONS = exports.CATALOGUE = exports.FIELD_ACCESS = exports.SCOPES = void 0;
exports.scopeRank = scopeRank;
exports.widerScope = widerScope;
exports.fieldAccessRank = fieldAccessRank;
exports.widerFieldAccess = widerFieldAccess;
/**
 * How far a grant reaches.
 *
 * ⚠️ **Ordered, and the order is the merge rule.** `none < own < workspace`.
 *
 * ⚠️ **THREE RUNGS, AND THE MOCKUP DRAWS FOUR — `decisions.md` R-13.** The dead legacy tab's
 * Hungarian ladder is `nincs < saját < csapat < workspace`, and `csapat` was on this list until it
 * was settled that **a team is not a concept in this product**: nothing anywhere defined who is in
 * one. While it existed the server resolved it to `own` and the Jogosultságok screen carried a
 * warning on every row that used it — a value whose only honest description was "means something
 * else". Migration 0013 removed it from the enum.
 *
 * If project-scoped visibility is wanted later — the PM description's *"a csapata óráit látja"* —
 * that is a **project** question answered by `project_members`, not a fourth rung here.
 */
exports.SCOPES = ['none', 'own', 'workspace'];
/** Rank, for comparison. Never compare scopes as strings. */
function scopeRank(scope) {
    return exports.SCOPES.indexOf(scope);
}
/**
 * The wider of two scopes.
 *
 * ⚠️ **"Most permissive wins" is CLAUDE.md's rule for merging a member's roles**, and it is the
 * opposite of the intuition people bring from filesystem ACLs, where a deny usually wins. Here a
 * member with two roles gets the union: if one role grants `worklog.read` at `team` and another
 * at `workspace`, they read the workspace. Stated because a future "restrictive role" feature
 * would contradict it and needs a decision, not a patch.
 */
function widerScope(a, b) {
    return scopeRank(a) >= scopeRank(b) ? a : b;
}
/** What a role may do to one field. Ordered, same merge rule. */
exports.FIELD_ACCESS = ['hidden', 'read', 'write'];
function fieldAccessRank(access) {
    return exports.FIELD_ACCESS.indexOf(access);
}
function widerFieldAccess(a, b) {
    return fieldAccessRank(a) >= fieldAccessRank(b) ? a : b;
}
/**
 * Every action, as `resource.action`.
 *
 * ⚠️ Grouped exactly as the mockup groups them, because those groups are what the redrawn
 * Jogosultságok screen renders — six sections, in this order.
 */
/**
 * ⚠️ **THE FIELD LIST IS ALMOST EMPTY, AND THAT IS THE HONEST STATE.**
 *
 * CLAUDE.md §3 says *"Resources declare actions and fields"*, and the field-rule machinery is
 * built and enforced — `fieldAccess`, `hiddenFields`, `CheckService.serialise` and
 * `validateWrite` all work. What does not exist is a catalogue of which columns are restrictable,
 * because **the mockup names none**: it has no hidden field, no read-only field, and no UI for
 * either.
 *
 * The one field with evidence is `task.internal_note`, which migration 0009 seeds as `hidden` for
 * the Alvállalkozó level. So that is the one declared here.
 *
 * ⚠️ Guessing the rest — `hourly_rate`, `cost_rate`, `margin` — would be inventing a schema, which
 * rule 8 forbids without flagging. Each arrives with the chunk that draws the column and therefore
 * knows whether it is sensitive: C7 for project money, C11 for rates. The Jogosultságok screen
 * renders the declared fields **plus whatever a role already has a rule for**, so a field added by
 * a migration ahead of this list still appears rather than becoming invisible.
 */
exports.CATALOGUE = {
    task: {
        label: 'Feladatok',
        /* The only field the project has evidence for — see the note above. */
        fields: ['internal_note'],
        actions: {
            /* Straight from the mockup: task.view, create, update, assign, schedule, delete. */
            read: 'task.view',
            create: 'task.create',
            update: 'task.update',
            /**
             * ⚠️ **P-1 lives on this action.** Assigning requires the assignee to be a member of the
             * task's project — *"you cannot assign a task to somebody who is not a member of its
             * project"*. That is a validation rule rather than a scope, so it is not expressible here;
             * `project-membership.ts` carries it and the service calls both.
             */
            assign: 'task.assign',
            schedule: 'task.schedule',
            delete: 'task.delete',
        },
    },
    worklog: {
        label: 'Időlogok',
        fields: [],
        actions: {
            /**
             * ⚠️ **COLLAPSED from three mockup keys.** `worklog.own`, `worklog.team` and `worklog.all`
             * are one action at `own`, `team` and `workspace`. The dead tab already had it as one
             * `worklog.view` with a scope per role, which is the evidence this is a reading rather than
             * a redesign.
             */
            read: 'worklog.read',
            create: 'worklog.create',
            /**
             * ⚠️ **COLLAPSED from `worklog.edit.any`.** "Any" is a scope word: the mockup's key means
             * "edit at workspace scope", and editing your own needs no separate permission — it is the
             * same action at `own`. So one action, and the scope says whose.
             */
            update: 'worklog.update',
            /** `worklog.lock` — setting the closing day. */
            lock: 'worklog.lock',
        },
    },
    project: {
        label: 'Projektek és ügyfelek',
        fields: [],
        actions: {
            read: 'project.view',
            /** `project.manage` in the mockup. Renamed to `update` for consistency; same key meaning. */
            update: 'project.manage',
            /**
             * ⚠️ **ADDED, and it needs a decision.** The mockup has no `project.create` — a project is
             * created from the sidebar's `+` with no permission check at all, because the single mockup
             * user is an Admin. `project.manage` covering creation is defensible; so is a separate
             * action. Recorded in `docs/plan/decisions.md` as open rather than settled here.
             */
            create: 'project.create',
            /**
             * ⚠️⚠️ **ADDED, AND `decisions.md` R-58 SETTLES IT — 13 Oct 2026, the product owner.** A
             * project's finance dashboard (the *Pénzügy* tab: revenue, cost, margin) answered to
             * `contract.view`, so whoever could read the contracts read the money. Its own action now, on
             * the project ladder: `workspace` every project, `own` the projects the reader is a member of
             * (P-1, as `project.view` reads it), `none` — and with `none` the tab is not drawn at all.
             * Migration `0201` granted it beside every `contract.view`, so nobody's access moved.
             */
            viewFinance: 'project.finance',
            /**
             * ⚠️⚠️ **ADDED, AND `decisions.md` R-46 SETTLES IT.** The mockup has one client key,
             * `client.manage`, and for two phases the client LIST stood on it — tolerable while the only
             * reader was the screen that edits clients. The agent broke that: it has to find a client to
             * create a project, and its registry gate reads any action that is not `*.view` as a write.
             * A lookup that could only be offered as a confirmation card is not a lookup.
             *
             * ⚠️ **Granted to exactly the roles that held `client.manage`** (migration 0158, custom roles
             * included), so the split moved nobody's access. Who ELSE may read clients is a later decision.
             */
            viewClients: 'client.view',
            /** `client.manage`. Clients live in this group in the mockup, so they stay here. */
            manageClients: 'client.manage',
        },
    },
    /**
     * C24. ⚠️ **The mockup declares this group and its three actions verbatim** — a `Szerződések`
     * group of `contract.view` (*Szerződések megtekintése*), `contract.manage` (*Szerződés
     * szerkesztése*) and `lineitem.manage` (*Sortételek kezelése*). Nothing here is invented.
     *
     * ⚠️ **Line items are managed by their OWN action, not by `contract.manage`**, and that is the
     * mockup's split rather than ours: its Projektmenedzser role holds `lineitem.manage` and
     * `contract.view` without `contract.manage` — somebody who may shape the work under a contract
     * but not the commercial terms of the contract itself.
     *
     * ⚠️ Every one of them is a **permission** question, which the feature flag is not. `contracts.view`
     * is what the tenant bought; these are who they are. Both are asked, and separately.
     */
    contract: {
        label: 'Szerződések',
        fields: [],
        actions: {
            read: 'contract.view',
            update: 'contract.manage',
            manageLineItems: 'lineitem.manage',
        },
    },
    people: {
        label: 'Dolgozók',
        fields: [],
        actions: {
            read: 'people.view',
            update: 'people.manage',
            /** `people.onboard` — invitation and the onboarding wizard. */
            onboard: 'people.onboard',
            /**
             * ⚠️ **ADDED, and it needs a decision.** `decisions.md` T-7 makes removal from a workspace
             * a *deactivation*, then a delete — a materially different act from editing somebody, and
             * the mockup's `people.manage` covers both because its only user could do everything.
             * Splitting it is the safer default; folding it back into `people.manage` is a decision.
             */
            deactivate: 'people.deactivate',
        },
    },
    /**
     * C47 · leave requests.
     *
     * ⚠️⚠️ **ADDED, AND FLAGGED UNDER RULE 8.** The mockup has no permission model of its own — its
     * `wsCan` is three hardcoded roles — so there is nothing to transcribe here. What the chunk's row
     * states is the RULE: who may decide a request is *this action, **or** being the requester's
     * manager*. The second half is not a permission and deliberately grants nothing else, exactly as
     * C31 settled for project roles; this action is the first half.
     *
     * ⚠️ **`read` is separate from `decide`** because *Kérelmek* - the list of everybody's — is a
     * screen somebody may be allowed to look at without being allowed to answer anything on it.
     * Filing and withdrawing one's OWN request needs neither: it is not an administrative act.
     */
    leave: {
        label: 'Szabadság',
        fields: [],
        actions: {
            read: 'leave.view',
            decide: 'leave.decide',
            /**
             * ⚠️⚠️ **ADDED, AND `decisions.md` R-48 SETTLES IT.** Filing your own leave needed no action at
             * all: `LeaveRequestService.create` asked for `leave.decide` only when the request was somebody
             * else's. That was defensible — filing is not an administrative act — and it made the agent's
             * `leave_requests_create` unbuildable, because gate 3 holds that a tool nobody granted anything
             * for must be a read (`registry.test.ts` argues why).
             *
             * ⚠️ **Nobody loses the ability to file.** Migration 0159 grants it to EVERY role that exists,
             * built-in and custom, because every role could file before. Filing for somebody ELSE still
             * needs `leave.decide`, unchanged.
             */
            request: 'leave.request',
        },
    },
    report: {
        label: 'Riportok',
        fields: [],
        actions: {
            read: 'report.view',
            /** `report.manage` — saving, renaming, duplicating, refreshing (P-3). */
            manage: 'report.manage',
            export: 'report.export',
            /**
             * ┌───────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ C60 · *Pénzes riportok* — the money MEASURES, separately.  │
             * └───────────────────────────────────────────────────────────────┘
             *
             * ⚠️⚠️ **THIS IS NOT `report.view` AND THE SPLIT IS THE WHOLE POINT.** Everybody who may
             * build a report may count hours and tasks; what a contract EARNS is a different kind of
             * fact, and a company that lets its whole delivery team group worklogs by project does not
             * thereby let them total the revenue by client.
             *
             * ⚠️ **It gates the MEASURE, not the source.** Somebody without it can still report on
             * contracts and line items — counting them, summing their hours — and simply cannot pick
             * revenue or budget. Gating the source instead would hide the structure along with the
             * money, which is not what anybody means by *"they should not see the figures"*.
             */
            money: 'report.money',
            /* ⚠️ 17 Oct 2026 · `risk.view` went with Kockázatkezelés (`0220`): its three tabs are reports
               now, behind `report.view` like every other. */
        },
    },
    /**
     * 20 Oct 2026 · Phase 6 · DM-1. ⚠️ **ADDED — the mockup draws no deal permission at all.**
     * `own` is a deal the member owns or follows. ⚠️ **No field rules**: seeing a deal is seeing its
     * value — the product owner's ruling, since the mockup draws nothing that hides it.
     */
    deal: {
        label: 'Dealek',
        fields: [],
        actions: {
            read: 'deal.view',
            manage: 'deal.manage',
        },
    },
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ 22 Oct 2026 · THE WORKSPACE MENU, ONE ROW EACH. ⚠️ ADDED under rule 8.   │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * The sidebar's *Munkaterület* rows were drawn for everybody, whatever they could see; a missing
     * read permission only took the count away. The product owner: one permission per row. **These
     * show or hide the ENTRANCE** — what a list then returns is still the resource's own read
     * permission and its scope (rule 6: UI gating is UX, the server check is the security). Any
     * scope but `none` shows the row. Every role that existed was granted them all (`0235`), so
     * nobody lost a row they had.
     */
    menu: {
        label: 'Munkaterület menü',
        fields: [],
        actions: {
            projects: 'menu.projects',
            tasks: 'menu.tasks',
            contracts: 'menu.contracts',
            invoices: 'menu.invoices',
            worklogs: 'menu.worklogs',
            people: 'menu.people',
            clients: 'menu.clients',
        },
    },
    /**
     * 22 Oct 2026 · ⚠️ ADDED under rule 8 — **one permission per dashboard.** They answered to
     * `project.view` and `worklog.read`, which a basic employee holds, so every dashboard opened for
     * everyone. Each is now its own grant, checked by the endpoint as well as the sidebar; a basic
     * employee is meant to see *Szabadságok* only. `0235` gave each to the roles that could open it.
     */
    dashboard: {
        label: 'Dashboardok',
        fields: [],
        actions: {
            resource: 'dashboard.resource',
            logging: 'dashboard.logging',
            utilization: 'dashboard.utilization',
            leave: 'dashboard.leave',
        },
    },
    settings: {
        label: 'Beállítások',
        fields: [],
        actions: {
            read: 'settings.view',
            update: 'settings.manage',
            manageIntegrations: 'integration.manage',
            /** `billing.manage`. Mockup-level only — `decisions.md` R-5, nothing charges anybody. */
            manageBilling: 'billing.manage',
            /**
             * ⚠️⚠️ **ADDED under rule 8 — `decisions.md` U-8.** Reading the workspace's whole audit log,
             * which no mockup draws. Granted to the built-in OWNER and ADMIN roles only (`0211`), and the
             * rows are **not** field-trimmed for whoever holds it: a narrower grant would leak money and
             * other people's leave through the diffs, so there is none. Under `settings` because the log
             * lives under workspace settings.
             */
            viewAudit: 'audit.view',
            /**
             * 22 Oct 2026 · ⚠️ **ADDED under rule 8.** Making another workspace in this tenant — the
             * workspace switcher's *Új munkaterület*. It answered to nothing, so a basic employee could.
             * The built-in OWNER and ADMIN roles hold it (`0236`); any other role on demand.
             */
            createWorkspace: 'workspace.create',
        },
    },
};
/** Every action key in the catalogue, flattened. Typed by the union above, not by inference. */
exports.ACTIONS = Object.values(exports.CATALOGUE).flatMap((resource) => Object.values(resource.actions));
/**
 * ⚠️ **Which mockup keys map onto which action.** Kept as data rather than prose so that
 * `__tests__/permissions.test.ts` can assert every one of the mockup's 25 keys is accounted for —
 * either mapped or explicitly dropped with a reason. Rule 8's real requirement is that nothing
 * disappears silently.
 */
exports.MOCKUP_KEY_MAP = {
    'task.view': 'task.view',
    'task.create': 'task.create',
    'task.update': 'task.update',
    'task.assign': 'task.assign',
    'task.schedule': 'task.schedule',
    'task.delete': 'task.delete',
    /* The collapse, stated as data. */
    'worklog.own': 'scope-of:worklog.read',
    'worklog.team': 'scope-of:worklog.read',
    'worklog.all': 'scope-of:worklog.read',
    'worklog.create': 'worklog.create',
    'worklog.edit.any': 'scope-of:worklog.update',
    'worklog.lock': 'worklog.lock',
    'project.view': 'project.view',
    'project.manage': 'project.manage',
    'client.manage': 'client.manage',
    'contract.view': 'contract.view',
    'contract.manage': 'contract.manage',
    'lineitem.manage': 'lineitem.manage',
    'people.view': 'people.view',
    'people.manage': 'people.manage',
    'people.onboard': 'people.onboard',
    'report.view': 'report.view',
    'report.manage': 'report.manage',
    'report.export': 'report.export',
    /**
     * ⚠️⚠️ **C60 · THE PHASE 4 MOCKUP'S KEY, SPELLED THE WAY IT SPELLS IT, AND IT WAS BRIEFLY
     * CLAIMED AS OURS INSTEAD.** It sat in `ADDED_ACTIONS` as well as here, under a comment saying
     * *"the prototype has no catalogue to take a spelling from"* — true of the Phase 1 and 2
     * mockups and not of this one. The Phase 4 prototype carries a full `PERM_CATALOG`:
     * `['report.money', 'Pénzes riportok (költség, bevétel, margó)']` in its *Riportok* group, and
     * the string again in each permission level's `perms` array. `canMoneyRep` reads that key; it is
     * not a substitute for one.
     *
     * ⚠️ Declared in two places at once, the catalogue contradicted itself and two cases in
     * `permissions.test.ts` failed — which is the guard working, not a test to be relaxed.
     */
    'report.money': 'report.money',
    'settings.view': 'settings.view',
    'settings.manage': 'settings.manage',
    'integration.manage': 'integration.manage',
    'billing.manage': 'billing.manage',
};
/**
 * Actions this model adds beyond what the mockups declare. Each needs a decision — see the notes
 * above.
 *
 * ⚠️ **A key the mockup spells belongs in `MOCKUP_KEY_MAP` and NOT here**, even when the mockup
 * reads it through a helper rather than naming it in prose. `report.money` was in both for one
 * commit; the test that caught it is the reason the two lists are data.
 */
exports.ADDED_ACTIONS = [
    'project.create',
    /* R-58 · the finance dashboard, apart from the contracts. Argued at the `project` resource. */
    'project.finance',
    /* R-46 · reading clients without editing them. Argued at the `project` resource. */
    'client.view',
    'people.deactivate',
    /* C47 · Phase 3 has no mockup permission model to transcribe: the prototype's `wsCan` is three
       hardcoded roles. Both are additions and both are argued at the `leave` resource. */
    'leave.view',
    'leave.decide',
    /* R-48 · filing one's own leave, so the agent can propose it. Argued at the `leave` resource. */
    'leave.request',
    /* U-8 · the workspace audit log, administrators only. Argued at the `settings` resource. */
    'audit.view',
    /* DM-1 · deal management. Argued at the `deal` resource. */
    'deal.view',
    'deal.manage',
    /* 22 Oct 2026 · the workspace menu and the dashboards, a permission each. Argued there. */
    'menu.projects',
    'menu.tasks',
    'menu.contracts',
    'menu.invoices',
    'menu.worklogs',
    'menu.people',
    'menu.clients',
    'dashboard.resource',
    'dashboard.logging',
    'dashboard.utilization',
    'dashboard.leave',
    /* 22 Oct 2026 · making another workspace. Argued at the `settings` resource. */
    'workspace.create',
];
//# sourceMappingURL=catalogue.js.map